import { log } from 'constants/config';

// Common bot user agents
const BOT_PATTERNS = [
  /bot/i,
  /spider/i,
  /crawl/i,
  /slurp/i,
  /mediapartners/i,
  /adsbot/i,
  /googlebot/i,
  /bingbot/i,
  /yandex/i,
  /baiduspider/i,
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /whatsapp/i,
  /slackbot/i,
  /telegram/i,
  /applebot/i,
  /pingdom/i,
  /uptimerobot/i,
  /lighthouse/i,
  /pagespeed/i,
  /gtmetrix/i,
  /ahrefsbot/i,
  /semrushbot/i,
  /dotbot/i,
  /mj12bot/i,
  /blexbot/i,
  /seznambot/i,
  /petalbot/i,
  /ia_archiver/i,
  /headless/i,
  /phantomjs/i,
  /selenium/i,
  /puppeteer/i,
  /playwright/i,
];

export const isBot = (): boolean => {
  if (typeof window === 'undefined') {
    log('[BotDetection] Server environment, returning false');
    return false;
  }

  if (process.env.TARGET_PLATFORM === 'mobile') {
    return false;
  }

  try {
    const userAgent = navigator.userAgent || '';
    log('[BotDetection] User Agent:', userAgent);

    // 1. Check for obvious bot patterns in user agent
    const isBotUserAgent = BOT_PATTERNS.some((pattern) => pattern.test(userAgent));
    if (isBotUserAgent) {
      log('[BotDetection] Bot pattern matched in user agent');
    }

    // 2. Check for webdriver (most reliable automation indicator)
    const webdriverCheck = navigator.webdriver === true;
    if (webdriverCheck) {
      log('[BotDetection] WebDriver detected');
    }

    // 3. CDP (Chrome DevTools Protocol) detection - modern and reliable
    const cdpDetected = detectCDP();
    if (cdpDetected) {
      log('[BotDetection] CDP detected');
    }

    // 4. Check for common automation framework indicators
    const automationSignals = detectAutomationSignals();
    if (automationSignals.length > 0) {
      log('[BotDetection] Automation signals detected:', automationSignals);
    }

    // 5. CONSERVATIVE detection logic - only flag with strong evidence
    // We require either:
    // - Clear bot user agent, OR
    // - Multiple strong automation indicators
    const strongAutomationIndicators = [webdriverCheck, cdpDetected].filter(Boolean).length;
    const hasAutomationSignals = automationSignals.length > 0;

    const result =
      isBotUserAgent ||
      strongAutomationIndicators >= 2 ||
      (strongAutomationIndicators >= 1 && hasAutomationSignals);

    log('[BotDetection] Detection results:', {
      isBotUserAgent,
      webdriverCheck,
      cdpDetected,
      automationSignals,
      strongIndicators: strongAutomationIndicators,
      finalResult: result,
    });

    return result;
  } catch (error) {
    log('[BotDetection] Error during detection, defaulting to false:', error);
    return false; // Default to NOT flagging as bot if there's an error
  }
};

// Modern CDP detection - catches Puppeteer, Playwright, etc.
const detectCDP = (): boolean => {
  try {
    let detected = false;
    const error = new Error();

    Object.defineProperty(error, 'stack', {
      get() {
        detected = true;
        return '';
      },
    });

    // This console.log is intentional - it triggers CDP serialization
    log('[BotDetection] CDP detected', error);

    return detected;
  } catch (e) {
    // If there's an error, don't flag as bot
    return false;
  }
};

// Check for common automation framework indicators
const detectAutomationSignals = (): string[] => {
  const signals: string[] = [];

  try {
    // Check for Playwright-specific globals
    if (typeof window !== 'undefined') {
      // @ts-ignore
      if (window.__playwright__binding__) {
        signals.push('playwright-binding');
      }
      // @ts-ignore
      if (window.__pwInitScripts) {
        signals.push('playwright-init');
      }
    }

    // Check for suspicious plugin absence (headless browsers often have 0 plugins)
    // But be very conservative - only flag if multiple other indicators present
    const pluginCount = navigator.plugins?.length || 0;
    if (pluginCount === 0) {
      // Don't add this as a signal by itself since incognito mode also has 0 plugins
      // Only log for debugging
      log('[BotDetection] Zero plugins detected (could be incognito or headless)');
    }

    // Check for missing WebRTC (common in headless browsers)
    // @ts-ignore
    if (
      typeof window !== 'undefined' &&
      !window.RTCPeerConnection &&
      !(window as any).webkitRTCPeerConnection
    ) {
      signals.push('no-webrtc');
    }

    // Check for suspicious screen dimensions (common in headless)
    if (screen.width === 0 || screen.height === 0) {
      signals.push('zero-screen');
    }
  } catch (e) {
    // If there's an error checking these, don't flag as bot
    log('[BotDetection] Error checking automation signals:', e);
  }

  return signals;
};

// Rate limiting for API calls
interface RateLimitData {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitData> = new Map();
  private readonly windowMs: number = 60 * 1000; // 1 minute window
  private readonly maxRequests: number = 30; // 30 requests per minute

  isAllowed(key: string): boolean {
    const now = Date.now();
    const limitData = this.limits.get(key);

    if (!limitData || now > limitData.resetTime) {
      this.limits.set(key, {
        count: 1,
        resetTime: now + this.windowMs,
      });
      log('[RateLimiter] New window for key:', key, 'Status: 1/' + this.maxRequests);
      return true;
    }

    const timeRemaining = limitData.resetTime - now;
    const secondsRemaining = Math.ceil(timeRemaining / 1000);

    if (limitData.count >= this.maxRequests) {
      log('[RateLimiter] ❌ RATE LIMIT EXCEEDED for key:', key, {
        count: limitData.count,
        max: this.maxRequests,
        resetsIn: secondsRemaining + 's',
      });
      return false;
    }

    limitData.count++;
    log('[RateLimiter] ✅ Request allowed for key:', key, {
      count: limitData.count,
      max: this.maxRequests,
      remaining: this.maxRequests - limitData.count,
      resetsIn: secondsRemaining + 's',
    });
    return true;
  }

  getStatus(key: string): { count: number; remaining: number; resetsIn: number } | null {
    const limitData = this.limits.get(key);
    if (!limitData) return null;

    const now = Date.now();
    const timeRemaining = limitData.resetTime - now;
    const secondsRemaining = Math.ceil(timeRemaining / 1000);

    return {
      count: limitData.count,
      remaining: Math.max(0, this.maxRequests - limitData.count),
      resetsIn: secondsRemaining,
    };
  }

  // Get all active rate limits for debugging
  getAllLimits(): Map<string, RateLimitData> {
    return this.limits;
  }

  // Clean up expired entries
  cleanup(): void {
    const now = Date.now();
    let cleaned = 0;
    for (const [key, data] of this.limits.entries()) {
      if (now > data.resetTime) {
        this.limits.delete(key);
        cleaned++;
      }
    }
    if (cleaned > 0) {
      log('[RateLimiter] Cleaned up', cleaned, 'expired entries');
    }
  }
}

export const apiRateLimiter = new RateLimiter();

// Clean up rate limiter every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => apiRateLimiter.cleanup(), 5 * 60 * 1000);

  // Expose rate limiter to window for debugging
  (window as any).__RATE_LIMITER__ = {
    limits: () => apiRateLimiter.getAllLimits(),
    status: (key: string) => apiRateLimiter.getStatus(key),
    summary: () => {
      const limits = apiRateLimiter.getAllLimits();
      const summary: any = {};
      limits.forEach((data, key) => {
        const status = apiRateLimiter.getStatus(key);
        summary[key] = status;
      });
      return summary;
    },
  };

  log('[RateLimiter] Debug interface available at window.__RATE_LIMITER__');
}

// Create a session ID for rate limiting
export const getSessionId = (): string => {
  if (typeof window === 'undefined') return 'server';

  let sessionId = sessionStorage.getItem('bounce_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('bounce_session_id', sessionId);
    log('[SessionID] Created new session:', sessionId);
  } else {
    log('[SessionID] Using existing session:', sessionId);
  }
  return sessionId;
};
